import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { Job, Recipient, RecipientRequestService } from '../../../communication/index';
import { HwcryptoService, HwcryptoCertificate } from '../../services/hwcrypto.service';
import { MobileSigningService, MobileOperator } from '../../services/mobile-signing.service';
import { SigningStatusService, SigningStatus, StatusPollResult } from '../../services/signing-status.service';
import { NotificationService } from '../../common/NotificationService';
import { ToolbarService } from '../../common/ToolbarEventService';
import { ToolbarEvent, ToolbarEventPageEnum } from '../../common/ToolbarEvent';
import { Authorization } from '../../authorization/authorization.component';
import { Router } from '@angular/router';

declare var $: any;

// Cookie configuration for signer preferences
const SIGNER_PREF_COOKIE = 'signer_preferences';
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Signer preference stored in cookie
 */
interface SignerPreference {
  method: 'usb' | 'mobile';
  phoneNumber?: string;
  operator?: string;
  lastUsed: number;
}

/**
 * Signing method types
 */
export type SigningMethod = 'usb' | 'mobile';

/**
 * Result emitted when signing completes
 */
export interface SigningResult {
  success: boolean;
  method: SigningMethod;
  status?: string;
  error?: string;
}

/**
 * Unified Digital Signer Modal Component
 *
 * This component provides a shared modal for digital signing operations,
 * supporting USB e-signature and Mobile signing methods.
 *
 * Used by both PAdES (PDF signing) and CAdES (container signing) components.
 */
@Component({
  selector: 'app-digital-signer-modal',
  templateUrl: './digital-signer-modal.component.html',
  styleUrls: ['./digital-signer-modal.component.css'],
  standalone: false
})
export class DigitalSignerModalComponent implements OnInit, OnDestroy {

  // === Inputs ===
  @Input() requestCode: string = '';
  @Input() theJob: Job | null = null;
  @Input() theRecipient: Recipient | null = null;
  @Input() signReturnURL: string | null = null;
  @Input() enabledMethods: SigningMethod[] = ['usb', 'mobile'];
  @Input() showReasonLocation: boolean = true;

  // === Outputs ===
  @Output() signComplete = new EventEmitter<SigningResult>();
  @Output() signCancelled = new EventEmitter<void>();
  @Output() signRejected = new EventEmitter<void>();

  // === State flags ===
  isSignDocumentClicked: boolean = false;
  isSignDocumentClickedMobile: boolean = false;
  showChooseCertificateText: boolean = false;
  afterPinTextEntered: boolean = false;
  signingTerminated: boolean = false;

  isMobilSignClicked: boolean = false;
  isMobilSignSecondPage: boolean = false;

  // Initial signing state flags (for first-time signers)
  isInitialSigning: boolean = false;
  isWaitingForRealFingerprint: boolean = false;

  onModalCancel: boolean = false;

  // === Form data ===
  signingReason: string = '';
  signingLocation: string = '';
  fingerPrint: string = '';

  mobilSignModel: { mobile: string; operator: string } = {
    mobile: '',
    operator: ''
  };

  // === Data from services ===
  operators: MobileOperator[] = [];

  // === Subscriptions ===
  private subscriptions: Subscription[] = [];

  // === Current certificate for USB signing ===
  private currentCertificate: HwcryptoCertificate | null = null;

  constructor(
    private hwcryptoService: HwcryptoService,
    private mobileSigningService: MobileSigningService,
    private signingStatusService: SigningStatusService,
    private recipientRequestApi: RecipientRequestService,
    private notificationService: NotificationService,
    private toolbarService: ToolbarService,
    private authorization: Authorization,
    private router: Router,
    public translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.operators = this.mobileSigningService.getOperators();
    this.initMobilFormData();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * Check if a signing method is enabled
   */
  isMethodEnabled(method: SigningMethod): boolean {
    return this.enabledMethods.includes(method);
  }

  /**
   * Check if signing controls should be disabled
   */
  get isSigningDisabled(): boolean {
    return this.isMobilSignSecondPage ||
           this.isMobilSignClicked ||
           this.isSignDocumentClicked ||
           this.isSignDocumentClickedMobile ||
           this.showChooseCertificateText ||
           this.afterPinTextEntered;
  }

  /**
   * Check if signing is in progress (to hide reason/location fields)
   */
  get isSigningInProgress(): boolean {
    return this.isSignDocumentClicked ||
           this.showChooseCertificateText ||
           this.afterPinTextEntered ||
           this.isMobilSignSecondPage ||
           this.isSignDocumentClickedMobile;
  }

  // ==================== USB E-ID Signing ====================

  /**
   * Initiate USB e-ID signing
   */
  async openEidSign(): Promise<void> {
    if (this.signingTerminated) {
      return;
    }

    this.isSignDocumentClicked = true;

    // Step 1: Get certificate from smart card
    this.isSignDocumentClicked = false;
    this.showChooseCertificateText = true;

    this.subscriptions.push(
      this.hwcryptoService.getCertificate().subscribe({
        next: (certificate) => {
          if (this.signingTerminated) {
            console.log('Signing cancelled by user');
            this.resetUsbSigningState();
            return;
          }

          console.log('Certificate received...');
          this.currentCertificate = certificate;
          this.showChooseCertificateText = false;
          this.isSignDocumentClicked = true;

          // Save USB signing preference
          this.saveSignerPreference(this.theRecipient?.name || '', {
            method: 'usb',
            lastUsed: Date.now()
          });

          // Step 2: Get token hash from server
          this.getTokenHashAndSign(certificate);
        },
        error: (error) => {
          console.error('Certificate error:', error?.message || error);
          this.notificationService.OpenEIdSigningError(error?.message || 'Certificate selection failed');
          this.resetUsbSigningState();
        }
      })
    );
  }

  /**
   * Get token hash from server and sign it
   */
  private getTokenHashAndSign(certificate: HwcryptoCertificate): void {
    this.subscriptions.push(
      this.recipientRequestApi.recipientRequestsCodeTokenhashPut(
        this.requestCode,
        certificate.hex,
        this.signingReason,
        this.signingLocation
      ).subscribe({
        next: (response: any) => {
          console.log('Token hash received...');
          const hash = response.resources?.[0]?.source || response.source;
          this.signWithHwcrypto(hash);
        },
        error: (err) => {
          console.error('Token hash error:', err);
          this.notificationService.displayError(err);
          this.resetUsbSigningState();
        }
      })
    );
  }

  /**
   * Sign the hash using hwcrypto
   */
  private signWithHwcrypto(hash: string): void {
    if (!this.currentCertificate) {
      this.notificationService.OpenEIdSigningError('No certificate selected');
      this.resetUsbSigningState();
      return;
    }

    this.subscriptions.push(
      this.hwcryptoService.sign(this.currentCertificate, hash).subscribe({
        next: (signature) => {
          console.log('Signature received...');
          this.afterPinTextEntered = true;
          this.isSignDocumentClicked = false;

          // Step 3: Send signature to server
          this.sendSignatureToServer(signature.hex);
        },
        error: (error) => {
          console.error('Signing failed:', error?.message || error);
          this.notificationService.OpenEIdSigningError(error?.message || 'Signing failed');
          this.resetUsbSigningState();
        }
      })
    );
  }

  /**
   * Send the signature to the server
   */
  private sendSignatureToServer(signatureHex: string): void {
    this.subscriptions.push(
      this.recipientRequestApi.recipientRequestsCodeTokensignaturePost(
        this.requestCode,
        signatureHex
      ).subscribe({
        next: (response) => {
          console.log('Signing successful...');
          this.handleSigningSuccess('usb');
        },
        error: (err) => {
          console.error('Signature submission error:', err);
          this.notificationService.displayError(err);
          this.resetUsbSigningState();
        }
      })
    );
  }

  /**
   * Reset USB signing state
   */
  private resetUsbSigningState(): void {
    this.isSignDocumentClicked = false;
    this.afterPinTextEntered = false;
    this.showChooseCertificateText = false;
    this.currentCertificate = null;
  }

  // ==================== Mobile Signing ====================

  /**
   * Initialize mobile sign form data
   */
  initMobilFormData(): void {
    this.mobilSignModel = this.mobileSigningService.createDefaultFormData();
  }

  /**
   * Initiate mobile signing
   */
  mobileSign(formData: { mobileNumber: string; operator: string }): void {
    console.log(`Mobile sign: ${formData.operator} ${formData.mobileNumber}`);
    this.isSignDocumentClickedMobile = true;

    // Save mobile signing preference
    this.saveSignerPreference(this.theRecipient?.name || '', {
      method: 'mobile',
      phoneNumber: formData.mobileNumber,
      operator: formData.operator,
      lastUsed: Date.now()
    });

    this.subscriptions.push(
      this.mobileSigningService.initiateMobileSign({
        requestCode: this.requestCode,
        operator: formData.operator,
        mobile: formData.mobileNumber,
        reason: this.signingReason,
        location: this.signingLocation
      }).subscribe({
        next: (result) => {
          if (result.success && result.formattedFingerPrint) {
            this.isMobilSignSecondPage = true;
            this.isMobilSignClicked = false;
            this.fingerPrint = result.formattedFingerPrint;

            if (result.isExpectInitialSigning) {
              // First-time signing - poll for real fingerprint
              this.isInitialSigning = true;
              this.isWaitingForRealFingerprint = true;
              this.pollForRealFingerprint();
            } else {
              // Normal flow - start status polling
              this.startStatusPolling('Mobil');
            }
          } else {
            this.notificationService.signingMobileSignNotFound();
            this.resetMobileSigningState();
          }
        },
        error: (err) => {
          console.error('Mobile sign error:', err);
          this.notificationService.displayError(err);
          this.resetMobileSigningState();
        }
      })
    );
  }

  /**
   * Handle mobile sign error or cancel
   */
  mobilSignErrororCancel(source: string): void {
    this.signingStatusService.stopPolling();

    if (source === 'timeout') {
      this.notificationService.signingTimeout(this.theJob?.name || '');
    }

    this.resetMobileSigningState();
  }

  /**
   * Reset mobile signing state
   */
  private resetMobileSigningState(): void {
    this.isMobilSignSecondPage = false;
    this.isSignDocumentClickedMobile = false;
    this.isMobilSignClicked = false;
    this.isInitialSigning = false;
    this.isWaitingForRealFingerprint = false;
    this.initMobilFormData();
  }

  /**
   * Poll for real fingerprint from signature_audit API.
   * Used for first-time signers where initial signing is required.
   */
  private pollForRealFingerprint(): void {
    const pollInterval = 2000; // 2 seconds
    const maxAttempts = 90; // 3 minutes max
    let attempts = 0;

    const poll = () => {
      if (this.signingTerminated || !this.isWaitingForRealFingerprint) {
        return;
      }

      attempts++;

      this.subscriptions.push(
        this.recipientRequestApi.recipientRequestsCodeSignatureAuditGet(this.requestCode)
          .subscribe({
            next: (audit) => {
              if (audit?.hash) {
                // Got real fingerprint - update display and start regular polling
                this.fingerPrint = this.mobileSigningService.formatFingerprint(audit.hash);
                this.isWaitingForRealFingerprint = false;
                this.startStatusPolling('Mobil');
              } else if (attempts < maxAttempts) {
                // Keep polling
                setTimeout(poll, pollInterval);
              } else {
                // Timeout
                this.notificationService.signingTimeout(this.theJob?.name || '');
                this.resetMobileSigningState();
              }
            },
            error: (err) => {
              if (attempts < maxAttempts) {
                // Retry on error
                setTimeout(poll, pollInterval);
              } else {
                console.error('Signature audit polling failed:', err);
                this.notificationService.displayError(err);
                this.resetMobileSigningState();
              }
            }
          })
      );
    };

    // Start polling
    poll();
  }

  // ==================== Status Polling ====================

  /**
   * Start polling for signing status
   */
  private startStatusPolling(source: string): void {
    this.subscriptions.push(
      this.signingStatusService.startPolling(this.requestCode).subscribe({
        next: (result) => {
          if (this.onModalCancel) {
            // Polling paused by user
            return;
          }

          this.handleStatusResult(result, source);
        },
        error: (err) => {
          console.error('Status polling error:', err);
          this.hideModal();
          this.notificationService.displayError(err);
        }
      })
    );
  }

  /**
   * Handle status polling result
   */
  private handleStatusResult(result: StatusPollResult, source: string): void {
    console.log('Status:', result.status);

    if (result.isSuccess) {
      this.handleSigningSuccess('mobile');
    } else if (result.isRejected) {
      this.handleSigningRejected();
    } else if (result.status === SigningStatus.CERTIFICATE_EXPIRING_SOON) {
      this.notificationService.certificateExpiringSoon();
      this.mobilSignErrororCancel('');
    } else if (result.isError) {
      this.logToolbarEvent('WAITING', this.theJob?.status || '');
      this.notificationService.signingError(this.theJob?.name || '', result.status);
      this.mobilSignErrororCancel('');
    } else if (result.status === 'TIMEOUT') {
      this.mobilSignErrororCancel('timeout');
    }
  }

  // ==================== Common Handlers ====================

  /**
   * Handle successful signing
   */
  private handleSigningSuccess(method: SigningMethod): void {
    this.hideModal();

    const isAnonymous = !this.authorization.isInitializedAfterLogin();
    const isSelfJob = this.theJob?.type?.toUpperCase() === 'SELF';

    if (isAnonymous) {
      this.notificationService.showJobSigned(this.theRecipient?.isRegistered || '', (isConfirmed: boolean) => {
        if (isConfirmed) {
          if (this.signReturnURL) {
            // Redirect to return URL with job ID
            window.location.href = `${this.signReturnURL}?jobId=${this.theJob?.id}`;
          } else {
            this.router.navigate(['/doc', this.theJob?.id]);
          }
        }
      });
    } else {
      this.notificationService.jobSigned(this.theJob?.name || '');
    }

    // Log toolbar event
    const eventPageEnum = isSelfJob
      ? ToolbarEventPageEnum.JOB_SELF_SIGNING
      : ToolbarEventPageEnum.JOB_SIGN_REQUEST;

    if (isSelfJob) {
      this.notificationService.jobCompleted(this.theJob?.name || '');
      this.toolbarService.action(new ToolbarEvent(
        eventPageEnum,
        this.theJob?.type || '',
        this.theJob?.user?.id || 0,
        'SIGNED',
        this.theJob?.name || '',
        'COMPLETED',
        this.theJob?.location || '',
        '',
        ''
      ));
    } else {
      this.toolbarService.action(new ToolbarEvent(
        eventPageEnum,
        this.theJob?.type || '',
        this.theJob?.user?.id || 0,
        'SIGNED',
        this.theJob?.name || '',
        '',
        '',
        '',
        ''
      ));
    }

    // Emit success event
    this.signComplete.emit({
      success: true,
      method,
      status: 'SUCCESS'
    });
  }

  /**
   * Handle signing rejection
   */
  private handleSigningRejected(): void {
    this.hideModal();

    const isAnonymous = !this.authorization.isInitializedAfterLogin();

    if (isAnonymous) {
      this.notificationService.showJobRejected(this.theRecipient?.isRegistered || '', (isConfirmed: boolean) => {
        if (isConfirmed) {
          this.router.navigate(['/doc', this.theJob?.id]);
        }
      });
    } else {
      this.notificationService.jobRejected(this.theJob?.name || '');
    }

    // Log toolbar event
    this.logToolbarEvent('DECLINED', 'REJECTED');

    // Emit rejection event
    this.signRejected.emit();
  }

  /**
   * Log toolbar event
   */
  private logToolbarEvent(action: string, status: string): void {
    const eventPageEnum = this.theJob?.type?.toUpperCase() === 'SELF'
      ? ToolbarEventPageEnum.JOB_SELF_SIGNING
      : ToolbarEventPageEnum.JOB_SIGN_REQUEST;

    this.toolbarService.action(new ToolbarEvent(
      eventPageEnum,
      this.theJob?.type || '',
      this.theJob?.user?.id || 0,
      action,
      this.theJob?.name || '',
      status,
      '',
      '',
      ''
    ));
  }

  // ==================== Modal Control ====================

  /**
   * Handle back/close button click
   */
  goBack(): void {
    const activeTab = this.getActiveTab();

    if (activeTab === 'Mobile Sign') {
      if (this.isMobilSignSecondPage) {
        this.mobilSignErrororCancel('user cancelled');
      } else {
        this.confirmCancel();
      }
    } else {
      // USB E-Signature tab
      this.confirmCancel();
    }
  }

  /**
   * Get active tab name
   */
  private getActiveTab(): string {
    const activeTabElement = $('ul#tabs li.active a');
    if (activeTabElement.length > 0) {
      const text = activeTabElement.text().trim();
      // Try to match known tab names
      if (text.includes('Mobile') || text.includes('Mobil')) return 'Mobile Sign';
      if (text.includes('USB') || text.includes('E-Signature')) return 'USB E-Signature';
    }
    return 'USB E-Signature';
  }

  /**
   * Show cancel confirmation dialog
   */
  private confirmCancel(): void {
    this.signingTerminated = true;
    this.notificationService.showJobSigningCancelAlert((isConfirmed: boolean) => {
      if (isConfirmed) {
        this.signingCancel(isConfirmed);
        this.notificationService.jobCanceled(this.theJob?.name || '');
      } else {
        this.signingTerminated = false;
      }
    });
  }

  /**
   * Handle signing cancellation
   */
  signingCancel(isConfirmed: boolean): void {
    if (isConfirmed) {
      this.hideModal();
      this.signCancelled.emit();
    }
  }

  /**
   * Show the modal
   */
  showModal(): void {
    // Reset all state before showing
    this.resetAllState();

    const modal = $('#myModal');

    // Load signer preference after modal is fully shown (DOM ready)
    modal.one('shown.bs.modal', () => {
      this.loadSignerPreference();
    });

    modal.modal('show');
  }

  /**
   * Reset all signing state to initial values
   */
  private resetAllState(): void {
    // USB signing state
    this.isSignDocumentClicked = false;
    this.showChooseCertificateText = false;
    this.afterPinTextEntered = false;
    this.currentCertificate = null;

    // Mobile signing state
    this.isMobilSignClicked = false;
    this.isMobilSignSecondPage = false;
    this.isSignDocumentClickedMobile = false;
    this.fingerPrint = '';
    this.isInitialSigning = false;
    this.isWaitingForRealFingerprint = false;

    // Common state
    this.signingTerminated = false;
    this.onModalCancel = false;
    this.signingReason = '';
    this.signingLocation = '';

    // Reset mobile form
    this.initMobilFormData();

    // Stop any ongoing polling
    this.signingStatusService.stopPolling();

    // Reset tab to default (USB)
    setTimeout(() => {
      $('ul#tabs a[href="#open_eid_sign"]').tab('show');
    }, 0);
  }

  /**
   * Hide the modal
   */
  hideModal(): void {
    $('#myModal').modal('hide');
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    this.signingStatusService.stopPolling();
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }

  // ==================== Signer Preference Storage ====================

  /**
   * Normalize signer name for cookie key (lowercase + trim)
   */
  private normalizeName(name: string): string {
    return (name || '').toLowerCase().trim();
  }

  /**
   * Get signer preferences from cookie
   */
  private getSignerPreferences(): Record<string, SignerPreference> {
    try {
      const cookie = this.getCookie(SIGNER_PREF_COOKIE);
      if (!cookie) return {};
      // Decode UTF-8 safe base64
      const json = decodeURIComponent(escape(atob(cookie)));
      return JSON.parse(json);
    } catch {
      return {};
    }
  }

  /**
   * Save signer preference to cookie
   */
  private saveSignerPreference(name: string, pref: SignerPreference): void {
    if (!name) return;

    const prefs = this.getSignerPreferences();
    const normalizedName = this.normalizeName(name);
    prefs[normalizedName] = pref;

    const json = JSON.stringify(prefs);
    // Encode UTF-8 safe base64 (handles Turkish characters like ş, ö, ü, etc.)
    const base64 = btoa(unescape(encodeURIComponent(json)));
    this.setCookie(SIGNER_PREF_COOKIE, base64, COOKIE_EXPIRY_DAYS);
  }

  /**
   * Load signer preference on modal init
   */
  private loadSignerPreference(): void {
    if (!this.theRecipient?.name) return;

    const prefs = this.getSignerPreferences();
    const normalizedName = this.normalizeName(this.theRecipient.name);
    const pref = prefs[normalizedName];

    if (!pref) return;

    // Set the preferred tab and fill form data
    if (pref.method === 'mobile' && this.isMethodEnabled('mobile')) {
      // Fill mobile form with saved data first
      if (pref.phoneNumber) {
        this.mobilSignModel.mobile = pref.phoneNumber;
      }
      if (pref.operator) {
        this.mobilSignModel.operator = pref.operator;
      }

      // Activate mobile tab - modal is already shown at this point
      $('ul#tabs a[href="#mobil_sign"]').tab('show');
    }
    // USB is default, no action needed for USB preference
  }

  /**
   * Get cookie value by name
   */
  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  /**
   * Set cookie with expiry
   */
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }
}
